/****************************************************
 *
 * 页面读取作用域：固定请求归属并隔离失活页面。
 *
 * @author Francis
 * @since 2026-10-09
 **************************************************/
import { CanceledError } from 'axios'
import { getActiveResourceId } from './resourceContext.mjs'

let invocationContext = null
let readGeneration = 0
const scopes = new Set()

export const getPageReadGeneration = () => readGeneration
export const isPageReadCanceled = (error) => error?.code === 'ERR_CANCELED' || error?.name === 'AbortError'

/** 身份或权限变化后，排队和正在进行的页面读取一并失效。 */
export function invalidatePageReads() {
  readGeneration += 1
  scopes.forEach((scope) => scope.close())
  return readGeneration
}

/** 仅在同步发起请求时传递上下文，绝不跨 await 保存全局状态。 */
export function withPageRequestContext(context, startRequest) {
  const previous = invocationContext
  invocationContext = context
  try {
    return startRequest()
  } finally {
    invocationContext = previous
  }
}

export function createPageReadScope({
  loadingScope = null,
  resourceId = '',
  authGeneration = readGeneration,
  isCurrent = () => true,
  background = false,
  route = null,
} = {}) {
  const controller = new AbortController()
  let closed = false
  const scope = {
    loadingScope,
    resourceId: String(resourceId || ''),
    authGeneration,
    signal: controller.signal,
    background,
    route,
    isActive: () => !closed && authGeneration === readGeneration && isCurrent(),
    assertActive() {
      if (!scope.isActive()) throw new CanceledError('页面读取已失效')
    },
    request(startRequest, options = {}) {
      scope.assertActive()
      const context = { ...scope, background: options.background ?? scope.background }
      return Promise.resolve(withPageRequestContext(context, startRequest)).then((result) => {
        scope.assertActive()
        return result
      })
    },
    close() {
      if (closed) return
      closed = true
      controller.abort()
      scopes.delete(scope)
    },
  }
  scopes.add(scope)
  return scope
}

/** POST 查询也属于读取；业务写入与导出不因菜单切换自动取消。 */
export function isPageReadRequest(options) {
  if (options.pageRead !== undefined) return Boolean(options.pageRead)
  const operation = String(options.url || '').split(/[?#]/)[0].split('/').pop() || ''
  if (/^(?:export|download)/i.test(operation)) return false
  return String(options.method || 'get').toLowerCase() === 'get'
    || /^(?:query|get|find|list|load|preview|statistics|summary|options|session)/i.test(operation)
}

/** 同时保留调用方的取消信号，并在请求完成时清除监听器。 */
function combineSignals(first, second) {
  if (!first || !second || first === second) return { signal: first || second, cleanup: () => {} }
  const controller = new AbortController()
  const abort = () => controller.abort()
  if (first.aborted || second.aborted) abort()
  else {
    first.addEventListener('abort', abort, { once: true })
    second.addEventListener('abort', abort, { once: true })
  }
  return {
    signal: controller.signal,
    cleanup() {
      first.removeEventListener('abort', abort)
      second.removeEventListener('abort', abort)
    },
  }
}

/** HTTP 入口立即保存资源上下文，异步加密结束时不再读取当前菜单。 */
export function capturePageRequest(options) {
  const sourceContext = options.requestContext || invocationContext
  const generation = sourceContext?.authGeneration ?? readGeneration
  const isRead = Boolean(sourceContext && isPageReadRequest(options))
  const context = {
    resourceId: sourceContext?.resourceId ?? getActiveResourceId(),
    loadingScope: sourceContext?.loadingScope,
    background: Boolean(sourceContext?.background),
    authGeneration: generation,
    assertActive() {
      if (generation !== readGeneration) throw new CanceledError('登录或权限已变化')
      if (isRead) sourceContext.assertActive()
    },
  }
  const combined = combineSignals(options.signal, isRead ? sourceContext.signal : undefined)
  const { requestContext: ignoredContext, pageRead: ignoredRead, ...requestOptions } = options
  if (combined.signal) requestOptions.signal = combined.signal
  return { options: requestOptions, context, cleanup: combined.cleanup }
}

if (typeof window !== 'undefined') {
  window.addEventListener?.('auth-session-cleared', invalidatePageReads)
}
